import { notification } from 'antd';
import request from '../../assets/utils/http-request';
import { setHeader } from '../../assets/utils/lib';

/**
 * API layer for the Plan Management workspace (new model, /v2/admin/pm/*).
 * All calls sit behind plan_designer.manage — the page itself is gated, so
 * unlike the PA panel these surface errors normally.
 *
 * Every helper returns { ok, data?, message? } — no thrown errors to handle
 * in components.
 */

const call = async (method, endpoint, payload = {}, { silent = false } = {}) => {
  try {
    const path = `/v2/admin/pm/${endpoint.replace(/^\//, '')}`;
    const cfg = setHeader();
    let resp;
    if (method === 'get') {
      resp = await request.get(path, { ...cfg, params: payload });
    } else if (method === 'delete') {
      resp = await request.delete(path, cfg);
    } else {
      resp = await request[method](path, payload, cfg);
    }
    if (parseInt(resp?.data?.error)) {
      if (!silent) notification.error({ message: resp?.data?.message || 'Request failed' });
      return { ok: false, message: resp?.data?.message, data: resp?.data?.data, httpStatus: resp?.status };
    }
    return { ok: true, data: resp?.data?.data, message: resp?.data?.message };
  } catch (e) {
    const message = e?.response?.data?.message || e?.message || 'Request failed';
    if (!silent) notification.error({ message });
    return { ok: false, message, data: e?.response?.data?.data, httpStatus: e?.response?.status };
  }
};

const pmApi = {
  meta: () => call('get', 'meta', {}, { silent: true }),

  // Benefit catalog
  benefitItems: () => call('get', 'benefit-items'),
  createBenefitItem: (body) => call('post', 'benefit-items', body),
  updateBenefitItem: (id, body) => call('put', `benefit-items/${id}`, body),
  deleteBenefitItem: (id) => call('delete', `benefit-items/${id}`),

  // Plan design
  createPlan: (body) => call('post', 'plans', body),
  updatePlanDetails: (planId, body) => call('patch', `plans/${planId}`, body),
  archivePlan: (planId, body) => call('post', `plans/${planId}/archive`, body),
  unarchivePlan: (planId) => call('post', `plans/${planId}/unarchive`),
  design: (planId) => call('get', `plans/${planId}/design`),
  createPool: (planId, body) => call('post', `plans/${planId}/pools`, body),
  updatePool: (planId, poolId, body) => call('put', `plans/${planId}/pools/${poolId}`, body),
  retirePool: (planId, poolId) => call('delete', `plans/${planId}/pools/${poolId}`),
  setCoverage: (planId, benefitItemId, body) => call('put', `plans/${planId}/coverage/${benefitItemId}`, body),
  removeCoverage: (planId, benefitItemId) => call('delete', `plans/${planId}/coverage/${benefitItemId}`),
  setProfile: (planId, body) => call('put', `plans/${planId}/profile`, body),
  clearDesign: (planId) => call('post', `plans/${planId}/clear-design`),
  launchPlan: (planId) => call('post', `plans/${planId}/launch`),
  importLegacy: (planId, replace) => call('post', `plans/${planId}/import-legacy-limits`, { replace }),
  copyDesign: (planId, sourcePlanId, replace) => call('post', `plans/${planId}/copy-design`, { source_plan_id: sourcePlanId, replace }),

  // Bands & pricing
  bands: () => call('get', 'bands'),
  createBand: (body) => call('post', 'bands', body),
  updateBand: (id, body) => call('put', `bands/${id}`, body),
  deleteBand: (id) => call('delete', `bands/${id}`),
  planBands: (planId) => call('get', `plans/${planId}/bands`),
  setPrice: (planId, bandId, body) => call('put', `plans/${planId}/bands/${bandId}/price`, body),
  detachBand: (planId, bandId) => call('delete', `plans/${planId}/bands/${bandId}`),
  setOrgPrice: (planId, orgId, bandId, body) => call('put', `plans/${planId}/orgs/${orgId}/bands/${bandId}/price`, body),

  // Cutover switchboard
  state: (planId) => call('get', `plans/${planId}/state`),
  setState: (planId, body) => call('put', `plans/${planId}/state`, body),

  // Org-scoped bands (client addition + org plan management)
  orgBands: (orgId, planId) => call('get', `orgs/${orgId}/plans/${planId}/bands`),

  // Analytics
  revenue: (params) => call('get', 'analytics/revenue', params),
  encounterAnalytics: (params) => call('get', 'analytics/encounters', params),
  unmappedCounts: (codes) => call('post', 'unmapped-counts', { checkin_codes: codes }, { silent: true }),
  mlr: (params) => call('get', 'analytics/mlr', params),
  consumption: (params) => call('get', 'consumption', params),

  // Single pricing service (premium types / sub-bands / dependents)
  offeredBands: (params) => call('get', 'offered-bands', params),
  quote: (body) => call('post', 'quote', body),

  // Global name mappings (registry)
  mappingRegistry: (params) => call('get', 'mappings/registry', params || {}),
  // An existing mapping can only be MOVED (mapNames); removing one would break
  // historical consumption, so there is no remove call.
  // Map (or move) tariff item names to a Benefit Item; every line with those names,
  // at any stage, is re-stamped and consumption follows.
  mapNames: (benefitItemId, itemNames, source = 'registry') => call('post', 'mapping/map', { benefit_item_id: benefitItemId, item_names: itemNames, source }),
  mappingLines: (checkinCode) => call('get', 'mapping/lines', { checkin_code: checkinCode }),
};

export default pmApi;
